import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Box, CircularProgress, Grid, TablePagination, Typography } from "@mui/material";
import { AddOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import CourseCard from "./CourseCard";
import type { AcademyOptions, PaginatedCourses } from "./types";
import { FilterSelect, ListPanel, PrimaryButton, SearchBox } from "./AcademyUi";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

export default function AcademyCourses() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ACADEMY_MANAGE);
  const [data, setData] = useState<PaginatedCourses | null>(null);
  const [options, setOptions] = useState<AcademyOptions | null>(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    apiService.get<PaginatedCourses>("/api/lms/courses/", {
      search: search || undefined, category: category || undefined,
      page: page + 1, page_size: rowsPerPage,
    }).then((response) => { setData(response.data); setError(""); })
      .catch(() => setError("Could not load courses."))
      .finally(() => setLoading(false));
  }, [search, category, page, rowsPerPage]);

  useEffect(() => { const timer = window.setTimeout(load, 300); return () => window.clearTimeout(timer); }, [load]);
  useEffect(() => { apiService.get<AcademyOptions>("/api/lms/courses/options/").then((r) => setOptions(r.data)); }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <ListPanel>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22 }}>Available Courses</Typography>
            <Typography variant="body2" color="text.secondary">Courses available to your teams and role.</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <SearchBox value={search} onChange={(value) => { setSearch(value); setPage(0); }} placeholder="Search courses..." />
            <FilterSelect value={category} onChange={(value) => { setCategory(value); setPage(0); }} allLabel="All categories"
              options={(options?.categories ?? []).map((value) => ({ value, label: value }))} />
            {canManage && (
              <PrimaryButton startIcon={<AddOutlined />} onClick={() => navigate("/academy/admin/courses/create")}>Add Course</PrimaryButton>
            )}
          </Box>
        </Box>

        {error ? (
          <Box textAlign="center" py={4}><Typography color="error">{error}</Typography></Box>
        ) : loading && !data ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={{ color: "#4285F4" }} /></Box>
        ) : !data?.results.length ? (
          <Box textAlign="center" py={4}><Typography sx={{ color: "text.secondary" }}>No courses found</Typography></Box>
        ) : (
          <Grid container spacing={2.5} sx={{ opacity: loading ? 0.6 : 1 }}>
            {data.results.map((course) => (
              <Grid key={course.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}><CourseCard course={course} /></Grid>
            ))}
          </Grid>
        )}

        <TablePagination
          component="div"
          count={data?.count ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[12, 24, 48]}
          labelRowsPerPage="Courses per page:"
          sx={{ mt: 2, borderTop: "1px solid #E0E0E0", "& .MuiTablePagination-toolbar": { justifyContent: "center" } }}
        />
      </ListPanel>
    </motion.div>
  );
}
